// packages/encamp-shared/src/pipelines/wasteManifestRowFieldMap.ts

export type WasteManifestSortType = "text" | "numeric" | "date_mdy" | "bool";

export interface WasteManifestField {
  /** GraphQL field name AND DataGrid column id — must match what the UI uses. */
  graphqlField: string;
  /** JSON path within PipelineOutput.data. Single-element today; array left in for future nesting. */
  jsonPath: string[];
  /** Controls ORDER BY casting. */
  sortType: WasteManifestSortType;
  /** True → included in omnisearch defaultSearchFields ILIKE. */
  searchable: boolean;
  /** Omnisearch keyed filter shorthand, e.g. "reportable" → user types `reportable:true`. */
  keyed?: string;
  /** Human-readable label for UI columns and the omnisearch field list. */
  label: string;
  /** True → value lives in a real PipelineOutput column, not in `data` JSONB. Defaults to false. */
  isRealColumn?: boolean;
  /** True → value is stored as a JSON-encoded text array in `data`. Drives ILIKE-vs-OVERLAP SQL emission. */
  isArray?: boolean;
}

export const WASTE_MANIFEST_FIELD_MAP: readonly WasteManifestField[] = [
  // Text fields — searchable + sortable
  {
    graphqlField: "manifestTrackingNumber",
    jsonPath: ["manifestTrackingNumber"],
    sortType: "text",
    searchable: true,
    keyed: "mtn",
    label: "Manifest Tracking #",
  },
  {
    graphqlField: "generatorId",
    jsonPath: ["generatorId"],
    sortType: "text",
    searchable: true,
    keyed: "epaId",
    label: "Generator EPA ID",
  },
  {
    graphqlField: "generatorName",
    jsonPath: ["generatorName"],
    sortType: "text",
    searchable: true,
    label: "Generator Name",
  },
  {
    graphqlField: "generatorLocationCity",
    jsonPath: ["generatorLocationCity"],
    sortType: "text",
    searchable: true,
    keyed: "city",
    label: "Generator City",
  },
  {
    graphqlField: "generatorLocationState",
    jsonPath: ["generatorLocationState"],
    sortType: "text",
    searchable: true,
    keyed: "state",
    label: "Generator State",
  },
  {
    graphqlField: "generatorLocationStreet1",
    jsonPath: ["generatorLocationStreet1"],
    sortType: "text",
    searchable: true,
    label: "Generator Street 1",
  },
  {
    graphqlField: "generatorLocationStreet2",
    jsonPath: ["generatorLocationStreet2"],
    sortType: "text",
    searchable: true,
    label: "Generator Street 2",
  },
  {
    graphqlField: "generatorLocationStreetNo",
    jsonPath: ["generatorLocationStreetNo"],
    sortType: "text",
    searchable: true,
    label: "Generator Street No",
  },
  {
    graphqlField: "generatorLocationZip",
    jsonPath: ["generatorLocationZip"],
    sortType: "text",
    searchable: true,
    label: "Generator Zip",
  },
  {
    graphqlField: "desFacilityId",
    jsonPath: ["desFacilityId"],
    sortType: "text",
    searchable: true,
    label: "Dest Facility ID",
  },
  {
    graphqlField: "desFacilityName",
    jsonPath: ["desFacilityName"],
    sortType: "text",
    searchable: true,
    label: "Dest Facility Name",
  },
  {
    graphqlField: "vendorProfileNumber",
    jsonPath: ["vendorProfileNumber"],
    sortType: "text",
    searchable: true,
    keyed: "profile",
    label: "Vendor Profile #",
  },
  {
    graphqlField: "vendorProfileDescription",
    jsonPath: ["vendorProfileDescription"],
    sortType: "text",
    searchable: true,
    label: "Vendor Profile Description",
  },
  {
    graphqlField: "vendorSource",
    jsonPath: ["vendorSource"],
    sortType: "text",
    searchable: true,
    label: "Vendor Source",
  },
  {
    graphqlField: "vendorSiteId",
    jsonPath: ["vendorSiteId"],
    sortType: "text",
    searchable: true,
    label: "Vendor Site ID",
  },
  {
    graphqlField: "vendorComments",
    jsonPath: ["vendorComments"],
    sortType: "text",
    searchable: true,
    label: "Vendor Comments",
  },
  {
    graphqlField: "transporter1Id",
    jsonPath: ["transporter1Id"],
    sortType: "text",
    searchable: true,
    label: "Transporter 1 ID",
  },
  {
    graphqlField: "transporter1Name",
    jsonPath: ["transporter1Name"],
    sortType: "text",
    searchable: true,
    label: "Transporter 1 Name",
  },
  {
    graphqlField: "transporter2Id",
    jsonPath: ["transporter2Id"],
    sortType: "text",
    searchable: true,
    label: "Transporter 2 ID",
  },
  {
    graphqlField: "transporter2Name",
    jsonPath: ["transporter2Name"],
    sortType: "text",
    searchable: true,
    label: "Transporter 2 Name",
  },
  {
    graphqlField: "transporter3Id",
    jsonPath: ["transporter3Id"],
    sortType: "text",
    searchable: true,
    label: "Transporter 3 ID",
  },
  {
    graphqlField: "transporter3Name",
    jsonPath: ["transporter3Name"],
    sortType: "text",
    searchable: true,
    label: "Transporter 3 Name",
  },
  {
    graphqlField: "transporter4Id",
    jsonPath: ["transporter4Id"],
    sortType: "text",
    searchable: true,
    label: "Transporter 4 ID",
  },
  {
    graphqlField: "nonHazWasteDescription",
    jsonPath: ["nonHazWasteDescription"],
    sortType: "text",
    searchable: true,
    label: "Non-Haz Waste Description",
  },
  {
    graphqlField: "dotIdNumberDescription",
    jsonPath: ["dotIdNumberDescription"],
    sortType: "text",
    searchable: true,
    label: "DOT ID Number",
  },
  {
    graphqlField: "dotPrintedInformation",
    jsonPath: ["dotPrintedInformation"],
    sortType: "text",
    searchable: true,
    label: "DOT Printed Info",
  },
  {
    graphqlField: "qtyUnitOfMeasureCode",
    jsonPath: ["qtyUnitOfMeasureCode"],
    sortType: "text",
    searchable: true,
    keyed: "uom",
    label: "UoM",
  },
  {
    graphqlField: "containerTypeCode",
    jsonPath: ["containerTypeCode"],
    sortType: "text",
    searchable: true,
    keyed: "container",
    label: "Container Type",
  },
  {
    graphqlField: "classification",
    jsonPath: ["classification"],
    sortType: "text",
    searchable: true,
    keyed: "class",
    label: "Classification",
  },
  {
    graphqlField: "brSourceCode",
    jsonPath: ["brSourceCode"],
    sortType: "text",
    searchable: true,
    label: "BR Source Code",
  },
  {
    graphqlField: "brFormCode",
    jsonPath: ["brFormCode"],
    sortType: "text",
    searchable: true,
    label: "BR Form Code",
  },
  {
    graphqlField: "managementMethodCode",
    jsonPath: ["managementMethodCode"],
    sortType: "text",
    searchable: true,
    label: "Management Method Code",
  },
  {
    graphqlField: "finalManagementMethodCode",
    jsonPath: ["finalManagementMethodCode"],
    sortType: "text",
    searchable: true,
    label: "Final Management Method Code",
  },
  {
    graphqlField: "managementMethodDescription",
    jsonPath: ["managementMethodDescription"],
    sortType: "text",
    searchable: true,
    label: "Management Method Description",
  },

  // JSON-array text fields — searchable as substring; keyed via OVERLAP
  {
    graphqlField: "stateWasteCodes",
    jsonPath: ["stateWasteCodes"],
    sortType: "text",
    searchable: true,
    keyed: "wasteCode",
    label: "State Waste Codes",
    isArray: true,
  },
  {
    graphqlField: "federalWasteCodes",
    jsonPath: ["federalWasteCodes"],
    sortType: "text",
    searchable: true,
    keyed: "wasteCode",
    label: "Federal Waste Codes",
    isArray: true,
  },
  {
    graphqlField: "tags",
    jsonPath: ["tags"],
    sortType: "text",
    searchable: true,
    keyed: "tag",
    label: "Tags",
    isArray: true,
  },

  // Numeric — not searchable, sortable via numeric cast
  {
    graphqlField: "quantity",
    jsonPath: ["quantity"],
    sortType: "numeric",
    searchable: false,
    label: "Quantity",
  },
  {
    graphqlField: "wasteLineNumber",
    jsonPath: ["wasteLineNumber"],
    sortType: "numeric",
    searchable: false,
    label: "Waste Line #",
  },
  {
    graphqlField: "containerNumber",
    jsonPath: ["containerNumber"],
    sortType: "numeric",
    searchable: false,
    label: "Container #",
  },
  {
    graphqlField: "brDensity",
    jsonPath: ["brDensity"],
    sortType: "numeric",
    searchable: false,
    label: "BR Density",
  },

  // Date MM/DD/YYYY — not searchable, sortable via TO_DATE
  {
    graphqlField: "shippedDate",
    jsonPath: ["shippedDate"],
    sortType: "date_mdy",
    searchable: false,
    keyed: "shipped",
    label: "Shipped Date",
  },
  {
    graphqlField: "receivedDate",
    jsonPath: ["receivedDate"],
    sortType: "date_mdy",
    searchable: false,
    keyed: "received",
    label: "Received Date",
  },

  // Booleans — not searchable, sortable as strings; keyed for explicit filtering
  {
    graphqlField: "reportable",
    jsonPath: ["reportable"],
    sortType: "bool",
    searchable: false,
    keyed: "reportable",
    label: "Reportable",
  },
  {
    graphqlField: "dotHazardous",
    jsonPath: ["dotHazardous"],
    sortType: "bool",
    searchable: false,
    keyed: "dotHaz",
    label: "DOT Hazardous",
  },
  {
    graphqlField: "epaWaste",
    jsonPath: ["epaWaste"],
    sortType: "bool",
    searchable: false,
    keyed: "epa",
    label: "EPA Waste",
  },
  {
    graphqlField: "isProvisional",
    jsonPath: ["isProvisional"],
    sortType: "bool",
    searchable: false,
    keyed: "prov",
    label: "Provisional",
  },
  {
    graphqlField: "overrideManualEdits",
    jsonPath: ["overrideManualEdits"],
    sortType: "bool",
    searchable: false,
    keyed: "overridden",
    label: "Override Manual Edits",
  },
];

/** Drill-in adds two real PipelineOutput columns (not JSON). */
export const REJECTED_DRILL_IN_EXTRA_FIELDS: readonly WasteManifestField[] = [
  {
    graphqlField: "rejectionReason",
    jsonPath: [],
    sortType: "text",
    searchable: true,
    keyed: "reason",
    label: "Rejection Reason",
    isRealColumn: true,
  },
  {
    graphqlField: "rejectionCategory",
    jsonPath: [],
    sortType: "text",
    searchable: true,
    keyed: "category",
    label: "Rejection Category",
    isRealColumn: true,
  },
];

/** Produce the ORDER BY expression for a field. NULLs are applied separately by the caller. */
export function sortExpressionFor(f: WasteManifestField): string {
  const path = f.isRealColumn
    ? `po.${quoteIdent(f.graphqlField)}`
    : `(po.data->>'${f.jsonPath[0]}')`;
  switch (f.sortType) {
    case "text":
    case "bool":
      return path;
    case "numeric":
      return `CASE WHEN ${path} ~ '^-?[0-9]+(\\.[0-9]+)?$' THEN ${path}::numeric END`;
    case "date_mdy":
      // Accept 1- or 2-digit month/day (e.g. `4/15/2026` as well as `04/15/2026`)
      // so unpadded extractor output still sorts chronologically instead of
      // sinking to NULL. TO_DATE with `MM/DD/YYYY` tolerates the unpadded form.
      return `CASE WHEN ${path} ~ '^[0-9]{1,2}/[0-9]{1,2}/[0-9]{4}$' THEN TO_DATE(${path}, 'MM/DD/YYYY') END`;
  }
}

/** Produce the column expression for omnisearch defaultSearchFields ILIKE matching. */
export function searchExpressionFor(f: WasteManifestField): string {
  if (f.isRealColumn) return `po.${quoteIdent(f.graphqlField)}`;
  return f.isArray ? `(po.data->'${f.jsonPath[0]}')::text` : `(po.data->>'${f.jsonPath[0]}')`;
}

/** True when the named field is stored as a JSON-encoded text array (per the registry). */
export function isArrayField(fieldName: string): boolean {
  const entry = WASTE_MANIFEST_FIELD_MAP.find((f) => f.graphqlField === fieldName);
  return !!entry?.isArray;
}

/** Aggregated-tab sort expressions. Throws on unknown so the resolver can return a clear error. */
export function groupSortExpressionFor(field: string): string {
  switch (field) {
    case "rejectionReason":
      return `po."rejectionReason"`;
    case "rejectionCategory":
      return `MIN(po."rejectionCategory")`;
    case "rowCount":
      return `COUNT(*)`;
    case "resolvedRowCount":
      return `COUNT(*) FILTER (WHERE po."resolvedAt" IS NOT NULL)`;
    default:
      throw new Error(`Unsortable group field: ${field}`);
  }
}

function quoteIdent(name: string): string {
  return `"${name}"`;
}

// `graphqlField` and `jsonPath` parts are spliced into raw SQL strings — they
// can't be parameterized (Prisma binds values, not column-side expressions).
// Enforce that every registry entry is a safe SQL identifier so a future entry
// with a quote or backslash fails at module load instead of emitting injectable
// SQL. The matching test in wasteManifestRowFieldMap.test.ts asserts this.
const SAFE_IDENT = /^[A-Za-z0-9_]+$/;
for (const f of [...WASTE_MANIFEST_FIELD_MAP, ...REJECTED_DRILL_IN_EXTRA_FIELDS]) {
  if (!SAFE_IDENT.test(f.graphqlField)) {
    throw new Error(
      `WasteManifestField.graphqlField is not a SQL-safe identifier: ${f.graphqlField}`
    );
  }
  for (const part of f.jsonPath) {
    if (!SAFE_IDENT.test(part)) {
      throw new Error(
        `WasteManifestField.jsonPath part is not a SQL-safe identifier: ${part} (on ${f.graphqlField})`
      );
    }
  }
}
